import {Dropdown} from "bootstrap";
import PageLoadedQueue from "../PageLoadedQueue";
import {asyncTimeout} from "../Functions/Timeout";

class DropdownHelper {
    private static instanceMap: Map<HTMLElement, DropdownHelper> = new Map();

    protected config = {
        popperConfig: {},
    };

    protected dropdownEl: HTMLElement;
    protected dropdown: Dropdown;
    protected dropdownTargetEl: HTMLElement;

    protected focused = false;
    protected clicked = false;

    constructor(dropdownEl: HTMLElement) {
        if (dropdownEl.dataset.plandentPlacement) {
            // @ts-ignore
            this.config.popperConfig.placement = dropdownEl.dataset.plandentPlacement;

            // overwrite the placement if the dropdown is inside the last element of the selector
            const lastUpSelector = dropdownEl.dataset.plandentDropdownLastUp;
            const lastUpPlacement = dropdownEl.dataset.plandentDropdownLastUpPlacement;
            const lastUpBoundrySelector = dropdownEl.dataset.plandentDropdownLastUpBoundry;

            if (lastUpSelector && lastUpPlacement && lastUpBoundrySelector) {
                const boundry = dropdownEl.closest(lastUpBoundrySelector);
                const lastUpItem = boundry.querySelector(lastUpSelector + ':last-child');

                if (lastUpItem === dropdownEl.closest(lastUpSelector)) {
                    // @ts-ignore
                    this.config.popperConfig.placement = lastUpPlacement;
                }
            }
        }

        this.dropdownEl = dropdownEl;
        this.dropdown = Dropdown.getOrCreateInstance(dropdownEl, this.config);
        this.dropdownTargetEl = dropdownEl.parentElement.querySelector('.dropdown-menu');

        dropdownEl.addEventListener('click', () => {
            const isInFilterBody = dropdownEl.closest('.filter-boxes__body');
            const shoppingCartList = dropdownEl.closest('.shopping-list.avency-nwd-shoppinglists-shoppingcart');

            if (isInFilterBody || shoppingCartList) {
                this.dropdown.toggle();
            } else {
                if (!this.focused && this.clicked) {
                    this.dropdown.hide();
                    this.clicked = false;
                    return;
                }
                if (this.focused) {
                    this.dropdown.toggle()
                } else {
                    this.dropdown.show();
                }
                this.clicked = true;
            }
        }, false);

        // fix to hide/show the dropdown on touch devices
        this.dropdownTargetEl.addEventListener('focusin', () => {
            this.focused = true;
        })

        this.dropdownEl.parentElement.addEventListener('focusout', () => {
            this.focused = false;
        })

        // Dropdown-Hover
        if (dropdownEl.classList.contains('dropdown-hover') || dropdownEl.parentElement.classList.contains('dropdown-hover')) {
            dropdownEl.parentElement.addEventListener('mouseenter', () => {
                this.show();
            }, false);

            dropdownEl.parentElement.addEventListener('mouseleave', () => {
                asyncTimeout(() => {
                    if (
                        !(dropdownEl.closest('.dropdown-hover') === dropdownEl.closest('.dropdown-hover:hover'))
                        && !this.focused
                    ) {
                        this.hide();
                    }
                }, this.hideDelay);
            }, false);
        }

        dropdownEl.dataset.plandentDropdownInit= "1";
    }

    public toggle = () => {
        this.dropdown.toggle();
    }

    public hide = () => {
        this.focused = false;
        this.clicked = false;
        this.dropdown.hide();
    }

    public show = () => {
        this.dropdown.show();
    }

    public dispose = () => {
        this.dropdown.dispose();
    }

    public update = () => {
        this.dropdown.update();
    }

    public getDropdown = () => {
        return this.dropdown;
    }

    public getDropdownElement = () => {
        return this.dropdownEl;
    }

    /**
     * Get the hide delay from the data-bs-delay attribute
     *
     * @protected
     */
    protected get hideDelay() {
        let hideDelay = this.dropdownEl.dataset.bsDelay
            // @ts-ignore
            ? JSON.parse(this.dropdownEl.dataset.bsDelay)
            : null;

        return hideDelay?.hide ?? 100;
    }

    /**
     * Get or create a new dropdown
     *
     * @param dropdownEl
     */
    public static getOrCreateInstance = (dropdownEl: HTMLElement): DropdownHelper => {
        if (!DropdownHelper.instanceMap.has(dropdownEl)) {
            DropdownHelper.instanceMap.set(dropdownEl, new DropdownHelper(dropdownEl));
        }

        return DropdownHelper.instanceMap.get(dropdownEl);
    }

    /**
     * Create Dropdown-Instances from a NodeList of HTMLSelectElements or a selector string
     *
     * @param {NodeListOf<HTMLSelectElement>|string} elementList
     */
    static createInstances = (elementList:NodeListOf<HTMLSelectElement>|string) => {
        if (typeof elementList === 'string') {
            elementList = document.querySelectorAll(elementList);
        }

        if (elementList.length > 0) {
            Array.from(elementList).forEach((el) => {
                DropdownHelper.getOrCreateInstance(el);
            });
        }
    }

    static defaultInit = () => {
        const bootstrapLegacy = '[data-toggle="dropdown"]:not([data-plandent-dropdown-init="1"])';
        DropdownHelper.createInstances(bootstrapLegacy);

        const bootstrapNew = '[data-bs-toggle="dropdown"]:not([data-plandent-dropdown-init="1"])';
        DropdownHelper.createInstances(bootstrapNew);

        const plandentDropdown = '[data-plandent-toggle="dropdown"]:not([data-plandent-dropdown-init="1"])';
        DropdownHelper.createInstances(plandentDropdown);
    }
}

PageLoadedQueue.addFunction(DropdownHelper.defaultInit);

document.addEventListener('plandent.reinit.bootstrapDropdown', DropdownHelper.defaultInit, {passive: true});

export default DropdownHelper;
